/**
 * Duplicate vehicles — a shop administrator reviews vehicle records of this shop
 * that share one verified VIN, and may merge a duplicate into the record kept.
 *
 * Nothing merges by itself: a matching VIN is strong evidence, not a decision.
 * The administrator sees each record's evidence side by side (the same
 * OptiVehicle profile data used everywhere), chooses the record to keep, reads
 * exactly what would move and what stays (vehicle_merge_preview), and confirms.
 * The merge itself (merge_duplicate_vehicle) is one audited, idempotent, all-or-
 * nothing transaction that re-checks everything under lock. "Decide later"
 * leaves both records untouched.
 *
 * Authority: Owner / Manager / Org Admin (route guard + the database functions,
 * which refuse anyone else).
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Car, CheckCircle2, Loader2 } from 'lucide-react';
import { useVehicleProfile, vehicleProfileKey } from '@/hooks/useVehicleProfile';
import { latestMileage } from '@/components/vehicle-profile/mileage';

const db = supabase as unknown as { from: (t: string) => any; rpc: (fn: string, args: Record<string, unknown>) => any };

interface GroupRow { group_no: number; vehicle_id: string; created_at: string }

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const LETTERS = 'ABCDEFGH';

const MOVE_LABELS: Record<string, string> = {
  tickets: 'Visits (tickets, including deleted ones, with their mileage and correction trail)',
  service_recommendations: 'Service recommendations',
  chronicle_threads: 'Customer message threads',
  chronicle_notifications: 'Customer notifications',
  learned_timing: 'Learned job timing',
  imported_vehicles: 'Imported vehicle records (re-linked by VIN)',
  imported_repair_orders: 'Imported repair orders',
  camera_tracks: 'Current camera-track matches',
};
const BLOCKER_TEXT: Record<string, string> = {
  not_found: 'One of the records no longer exists.',
  same_vehicle: 'Choose two different records.',
  survivor_retired: 'The record to keep was itself merged into another record.',
  already_retired: 'This record has already been merged.',
  vin_identity_differs: 'The records do not share the same verified VIN, so they cannot be merged here.',
  learned_timing_overlap: 'Both records hold learned timing for the same job and technician. It cannot be combined automatically.',
};

export default function VehicleDuplicates() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id ?? null;
  const groupsQ = useQuery({
    queryKey: ['vehicle-duplicate-groups', shopId],
    enabled: !!shopId,
    queryFn: async () => {
      const { data, error } = await db.rpc('vehicle_duplicate_groups', { p_shop_id: shopId });
      if (error) throw error;
      return (data ?? []) as GroupRow[];
    },
  });
  const groups = useMemo(() => {
    const m = new Map<number, GroupRow[]>();
    for (const r of groupsQ.data ?? []) m.set(r.group_no, [...(m.get(r.group_no) ?? []), r]);
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([, rows]) => rows);
  }, [groupsQ.data]);

  return (
    <div className="container mx-auto max-w-6xl p-4 sm:p-6 space-y-4" data-testid="vehicle-duplicates-page">
      <div>
        <h1 className="text-2xl font-semibold">Duplicate vehicles</h1>
        <p className="text-sm text-muted-foreground">
          Vehicle records in this shop that share the same verified VIN. Compare the evidence, choose the record to keep,
          and merge only when you are sure they are the same physical vehicle. Nothing merges automatically.
        </p>
      </div>
      {groupsQ.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
      {groupsQ.isError && <p className="text-sm text-destructive" data-testid="vehicle-duplicates-error">Duplicate vehicles could not be loaded.</p>}
      {!groupsQ.isLoading && !groupsQ.isError && groups.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="vehicle-duplicates-empty">No duplicate vehicle records in this shop.</p>
      )}
      {groups.map((rows, i) => <DuplicateGroup key={rows.map((r) => r.vehicle_id).join(':')} index={i + 1} rows={rows} shopId={shopId!} />)}
    </div>
  );
}

function DuplicateGroup({ index, rows, shopId }: { index: number; rows: GroupRow[]; shopId: string }) {
  const [keepId, setKeepId] = useState<string | null>(null);
  const [mergeId, setMergeId] = useState<string | null>(null);
  const label = (id: string) => `Record ${LETTERS[rows.findIndex((r) => r.vehicle_id === id)] ?? '?'}`;
  return (
    <section className="rounded-lg border p-3 sm:p-4 space-y-3" data-testid={`duplicate-group-${index}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Same VIN · {rows.length} records</h2>
        <span className="text-xs text-muted-foreground">Choose the record to keep, then merge the others into it.</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((r) => (
          <DuplicateRecord
            key={r.vehicle_id}
            label={label(r.vehicle_id)}
            vehicleId={r.vehicle_id}
            createdAt={r.created_at}
            kept={keepId === r.vehicle_id}
            keepChosen={!!keepId}
            onKeep={() => setKeepId(r.vehicle_id)}
            onMerge={() => setMergeId(r.vehicle_id)}
          />
        ))}
      </div>
      {keepId && mergeId && (
        <MergeDialog
          shopId={shopId}
          survivorId={keepId}
          retiredId={mergeId}
          survivorLabel={label(keepId)}
          retiredLabel={label(mergeId)}
          onClose={() => setMergeId(null)}
        />
      )}
    </section>
  );
}

function DuplicateRecord({ label, vehicleId, createdAt, kept, keepChosen, onKeep, onMerge }: {
  label: string; vehicleId: string; createdAt: string; kept: boolean; keepChosen: boolean; onKeep: () => void; onMerge: () => void;
}) {
  const q = useVehicleProfile(vehicleId);
  const customer = useQuery({
    queryKey: ['duplicate-vehicle-customer', vehicleId],
    queryFn: async () => {
      const v = await db.from('vehicles').select('customer_id, updated_at').eq('id', vehicleId).maybeSingle();
      if (v.error || !v.data) return { name: null as string | null, updatedAt: null as string | null };
      if (!v.data.customer_id) return { name: null, updatedAt: v.data.updated_at as string };
      const c = await db.from('customers').select('full_name').eq('id', v.data.customer_id).maybeSingle();
      return { name: (c.data?.full_name as string | undefined) ?? 'Customer (not visible)', updatedAt: v.data.updated_at as string };
    },
  });
  // Deleted visits are not part of the profile history, but a merge moves them too —
  // and they can be the only activity a duplicate record has.
  const deletedVisits = useQuery({
    queryKey: ['duplicate-vehicle-deleted-visits', vehicleId],
    queryFn: async () => {
      const r = await db.from('tickets').select('id', { count: 'exact', head: true }).eq('vehicle_id', vehicleId).not('deleted_at', 'is', null);
      return r.error ? null : (r.count as number | null) ?? 0;
    },
  });
  const d = q.data;
  const latest = latestMileage(d?.mileage ?? []);
  const facts = d?.resolved.filter((f) => f.value != null && f.sourceClass && f.sourceClass !== 'shop_record') ?? [];
  return (
    <div className={`rounded-md border p-3 space-y-2 text-sm ${kept ? 'border-primary ring-1 ring-primary' : ''}`} data-testid={`duplicate-record-${label.slice(-1)}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-medium"><Car className="h-4 w-4 shrink-0" />{label}</div>
          <div className="text-xs text-muted-foreground">Created {fmtDate(createdAt)}{customer.data?.updatedAt ? ` · updated ${fmtDate(customer.data.updatedAt)}` : ''}</div>
        </div>
        {kept && <Badge className="shrink-0" data-testid="duplicate-kept-badge"><CheckCircle2 className="h-3 w-3 mr-1" />Keep</Badge>}
      </div>
      {q.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
          <dt className="text-muted-foreground">Vehicle</dt><dd className="break-words">{d?.summary.title}{d?.summary.specLine ? ` · ${d.summary.specLine}` : ''}</dd>
          <dt className="text-muted-foreground">Customer</dt><dd>{customer.data?.name ?? 'None linked'}</dd>
          <dt className="text-muted-foreground">Visits</dt><dd data-testid="duplicate-visits">{d?.history.length ?? 0}{d?.history[0] ? ` · last ${fmtDate(d.history[0].completedAt ?? d.history[0].createdAt)}` : ''}</dd>
          <dt className="text-muted-foreground">Deleted visits</dt><dd data-testid="duplicate-deleted-visits">{deletedVisits.data == null ? '—' : deletedVisits.data}</dd>
          <dt className="text-muted-foreground">Mileage</dt><dd>{latest ? `${latest.miles.toLocaleString()} mi · ${fmtDate(latest.observedAt)}` : 'None on record'}</dd>
          <dt className="text-muted-foreground">Imported</dt><dd>{d?.imported.length ?? 0} repair order{(d?.imported.length ?? 0) === 1 ? '' : 's'}</dd>
          <dt className="text-muted-foreground">Recorded facts</dt><dd>{facts.length === 0 ? 'None' : `${facts.length} (${[...new Set(facts.map((f) => f.sourceLabel))].join(', ')})`}</dd>
          <dt className="text-muted-foreground">Recommendations</dt><dd>{d?.recommendations.length ?? 0}</dd>
        </dl>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {!kept && (
          <Button type="button" size="sm" variant="outline" className="h-9" onClick={onKeep} data-testid="duplicate-keep">Keep this record</Button>
        )}
        {keepChosen && !kept && (
          <Button type="button" size="sm" className="h-9" onClick={onMerge} data-testid="duplicate-merge">Merge into kept record…</Button>
        )}
      </div>
    </div>
  );
}

interface Preview {
  ok: boolean;
  blockers: string[];
  moves?: Record<string, number>;
  retained?: { identity_facts: number; camera_observations: number; camera_identity_events: number; retired_has_customer: boolean; customers_differ: boolean };
}

function MergeDialog({ shopId, survivorId, retiredId, survivorLabel, retiredLabel, onClose }: {
  shopId: string; survivorId: string; retiredId: string; survivorLabel: string; retiredLabel: string; onClose: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  // ONE request key per review: a double-click or a retry after a lost response is the same merge.
  const [requestKey] = useState(() => crypto.randomUUID());
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const preview = useQuery({
    queryKey: ['vehicle-merge-preview', survivorId, retiredId],
    queryFn: async () => {
      const { data, error } = await db.rpc('vehicle_merge_preview', { p_shop_id: shopId, p_survivor_id: survivorId, p_retired_id: retiredId });
      if (error) throw error;
      return data as Preview;
    },
  });
  const p = preview.data;
  const moves = Object.entries(p?.moves ?? {}).filter(([, n]) => n > 0);

  const merge = async () => {
    setBusy(true);
    const { data, error } = await db.rpc('merge_duplicate_vehicle', {
      p_shop_id: shopId, p_survivor_id: survivorId, p_retired_id: retiredId, p_request_key: requestKey, p_reason: reason.trim() || null,
    });
    setBusy(false);
    if (error) {
      toast({ title: 'Merge not completed', description: 'Nothing was changed. Review the records and try again.', variant: 'destructive' });
      await preview.refetch();
      return;
    }
    toast({ title: (data as { status?: string })?.status === 'already_merged' ? 'Already merged' : 'Records merged', description: `${retiredLabel} now redirects to ${survivorLabel}.` });
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['vehicle-duplicate-groups'] }),
      qc.invalidateQueries({ queryKey: vehicleProfileKey(survivorId) }),
      qc.invalidateQueries({ queryKey: vehicleProfileKey(retiredId) }),
    ]);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge {retiredLabel} into {survivorLabel}?</DialogTitle>
          <DialogDescription>
            {retiredLabel} will be kept as a retired record that points to {survivorLabel}. Nothing is deleted.
          </DialogDescription>
        </DialogHeader>
        {preview.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
        {p && !p.ok && (
          <div className="rounded-md border border-destructive/50 p-3 text-sm space-y-1" data-testid="merge-blockers">
            <div className="flex items-center gap-1 font-medium text-destructive"><AlertTriangle className="h-4 w-4" />This merge cannot run</div>
            {p.blockers.map((b) => <p key={b}>{BLOCKER_TEXT[b] ?? b}</p>)}
          </div>
        )}
        {p?.ok && (
          <div className="space-y-3 text-sm">
            <div data-testid="merge-moves">
              <h3 className="font-medium mb-1">Moves to {survivorLabel}</h3>
              {moves.length === 0 ? <p className="text-muted-foreground">Nothing is attached to {retiredLabel}.</p> : (
                <ul className="list-disc pl-5 space-y-0.5">{moves.map(([k, n]) => <li key={k}>{MOVE_LABELS[k] ?? k}: {n}</li>)}</ul>
              )}
            </div>
            <div data-testid="merge-retained">
              <h3 className="font-medium mb-1">Stays unchanged</h3>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>{survivorLabel}'s VIN, plate, year/make/model and customer are not changed.</li>
                <li>{retiredLabel}'s recorded vehicle facts stay on {retiredLabel}, with their sources ({p.retained?.identity_facts ?? 0}).</li>
                {(p.retained?.camera_observations ?? 0) + (p.retained?.camera_identity_events ?? 0) > 0 && (
                  <li>Camera history stays on {retiredLabel} and is reached through the redirect.</li>
                )}
                {p.retained?.retired_has_customer && (
                  <li>{retiredLabel}'s customer link stays on {retiredLabel}{p.retained.customers_differ ? ` (a different customer from ${survivorLabel}'s — ownership is not changed)` : ''}.</li>
                )}
              </ul>
            </div>
            <Textarea placeholder="Note (optional) — why these are the same vehicle" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="merge-reason" />
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={confirmed} onCheckedChange={(c) => setConfirmed(c === true)} data-testid="merge-confirm-check" className="mt-0.5" />
              <span>I reviewed the evidence and confirm {retiredLabel} and {survivorLabel} are the same physical vehicle.</span>
            </label>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} data-testid="merge-defer">Decide later</Button>
          <Button type="button" onClick={merge} disabled={!p?.ok || !confirmed || busy} data-testid="merge-submit">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Merge records
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
